// Consumo OBSERVADO de uma UC — média simples das faturas reais já emitidas.
//
// Vive em `_shared/` porque duas partes precisam do MESMO número: a rotina
// diária que grava `unidades_consumidoras.consumo_observado_kwh`, e o front do
// Rateio, que recalcula na tela a partir do histórico que já carregou. Se cada
// lado tivesse a sua cópia, elas divergiriam na primeira mudança de filtro — e
// o número decide quanta energia vai para cada UC (`INV-02` exige que o rateio
// feche exatamente). Mesmo motivo de `_shared/splitParceira.ts`.
//
// Módulo PURO — sem rede, sem Date, sem Deno-only.
//   node --test supabase/functions/_shared/__tests__/consumoObservado.test.ts

export const MESES_ORDEM = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** 'jan/26', '012026', 'JAN.2026' → 'JAN/2026'. Devolve a entrada crua se não reconhecer. */
export function normalizarMesReferencia(m: unknown): string {
  if (!m) return '';
  const mStr = String(m).trim().toUpperCase().replace(/\./g, '').replace(/\s/g, '');
  let mes = '';
  let ano = '';

  if (mStr.includes('/')) {
    const parts = mStr.split('/');
    if (parts.length === 2) [mes, ano] = parts;
  } else if (/^\d{5,6}$/.test(mStr)) {
    if (mStr.length === 6) {
      mes = mStr.substring(0, 2);
      ano = mStr.substring(2);
    } else {
      mes = mStr.substring(0, 1);
      ano = mStr.substring(1);
    }
  } else {
    const match = mStr.match(/^([A-Z]+|\d+)(\d{2,4})$/);
    if (match) [, mes, ano] = match;
    else return mStr;
  }

  if (!mes || !ano) return mStr;

  if (/^\d{1,2}$/.test(mes)) {
    const idx = parseInt(mes, 10) - 1;
    if (idx >= 0 && idx < 12) mes = MESES_ORDEM[idx];
  } else {
    const found = MESES_ORDEM.findIndex((n) => mes.startsWith(n));
    if (found !== -1) mes = MESES_ORDEM[found];
  }

  if (ano.length === 2) ano = `20${ano}`;
  if (ano.length !== 4 || !MESES_ORDEM.includes(mes)) return mStr;

  return `${mes}/${ano}`;
}

/**
 * 'JUL/2026' → 2026*12 + 6. `mes_referencia` é TEXT e ordenação alfabética não é
 * cronológica entre anos ('JAN/2027' < 'MAR/2026' no alfabeto). Null quando não
 * reconhece — nunca lança.
 */
export function mesReferenciaParaOrdinal(mesRef: string | null | undefined): number | null {
  const norm = normalizarMesReferencia(mesRef);
  if (!norm || !norm.includes('/')) return null;
  const [mes, anoStr] = norm.split('/');
  const idx = MESES_ORDEM.indexOf(mes);
  const ano = parseInt(anoStr, 10);
  if (idx === -1 || !Number.isFinite(ano)) return null;
  return ano * 12 + idx;
}

/** 24312 → 'JAN/2026'. */
export function rotuloDeOrdinal(ordinal: number): string {
  const ano = Math.floor(ordinal / 12);
  const mes = ((ordinal % 12) + 12) % 12;
  return `${MESES_ORDEM[mes]}/${ano}`;
}

/** Uma fatura, como sai do banco. */
export interface FaturaObservada {
  mes_referencia?: string | null;
  consumo_ativo_kwh?: number | string | null;
}

/** Uma observação mensal já limpa. */
export interface ObservacaoMensal {
  ordinal: number;
  consumoKwh: number;
  injecaoKwh?: number | null;
}

export interface ObservacoesLimpas {
  observacoes: ObservacaoMensal[];
  /** Faturas do mesmo mês já contabilizado — contar 2× deslocaria a média (`FIN-R09`). */
  duplicatasIgnoradas: number;
  /** Mês irreconhecível ou consumo ausente — nunca somem calados (`INV-01`). */
  mesesNaoReconhecidos: number;
}

/**
 * Converte faturas cruas em observações mensais: normaliza o mês, descarta o que
 * não dá para ler, e deduplica por mês.
 *
 * Consumo AUSENTE (null) e consumo MEDIDO como zero são coisas diferentes: o
 * primeiro é dado ilegível e é reportado; o segundo é leitura válida e apenas
 * não entra na média (UC vazia não descreve consumo).
 */
export function limparObservacoes(faturas: FaturaObservada[] | null | undefined): ObservacoesLimpas {
  const lista = Array.isArray(faturas) ? faturas : [];
  const porOrdinal = new Map<number, ObservacaoMensal>();
  let duplicatasIgnoradas = 0;
  let mesesNaoReconhecidos = 0;

  // Mês repetido: fica o MAIOR consumo, sempre.
  //
  // Repetição acontece de verdade — a UC pode ter dois números (`numero_uc` e
  // `outro_numero_uc`) e as faturas dos dois entram no mesmo balde. Ficar com "a
  // primeira que chegou" dependeria da ordem da resposta do banco, que não é
  // garantida: a mesma UC daria números diferentes entre execuções.
  //
  // O critério do maior não é arbitrário: a duplicata típica é uma linha-toco ao
  // lado da fatura real (na UC 691, ABR/2026 tinha 3,25 kWh e 8.381 kWh no mesmo
  // mês). Deixar o toco vencer afundaria a média e faria a UC receber menos
  // energia do que consome.
  const ordenadas = [...lista].sort(
    (a, b) => (Number(b?.consumo_ativo_kwh) || 0) - (Number(a?.consumo_ativo_kwh) || 0),
  );

  for (const f of ordenadas) {
    const ordinal = mesReferenciaParaOrdinal(f?.mes_referencia);
    if (ordinal == null) {
      mesesNaoReconhecidos++;
      continue;
    }
    const bruto = f?.consumo_ativo_kwh as unknown;
    if (bruto === null || bruto === undefined || bruto === '') {
      mesesNaoReconhecidos++;
      continue;
    }
    // Coluna `numeric` do Postgres chega como STRING pelo supabase-js.
    const consumoKwh = Number(bruto);
    if (!Number.isFinite(consumoKwh)) {
      mesesNaoReconhecidos++;
      continue;
    }
    if (porOrdinal.has(ordinal)) {
      duplicatasIgnoradas++;
      continue;
    }
    porOrdinal.set(ordinal, { ordinal, consumoKwh });
  }

  return {
    observacoes: [...porOrdinal.values()].sort((a, b) => a.ordinal - b.ordinal),
    duplicatasIgnoradas,
    mesesNaoReconhecidos,
  };
}

export interface MediaObservada {
  /** Média simples, sem viés. 0 quando não há mês válido. */
  kwh: number;
  /** Quantos meses entraram na média. */
  meses: number;
  /**
   * Coeficiente de variação (desvio ÷ média) dos meses considerados.
   *
   * É o detector de dado ruim. Sazonalidade real em Goiás fica em 0,15–0,25; uma
   * UC com 0,69 não é sazonal, é fatura corrompida — foi assim que a UC 438
   * (residencial B1) apareceu com dois meses de 67.072 kWh idênticos ao lado de
   * um mês de 916. Sem este número, aquele 45.020 kWh de média entraria no rateio
   * como se fosse verdade.
   */
  dispersao: number;
}

/** Acima disto os meses discordam demais entre si para o número alocar energia. */
export const DISPERSAO_INACEITAVEL = 0.6;

/**
 * Média SIMPLES do consumo observado — deliberadamente sem a regra pró-cliente
 * de `analiseTecnicaConsumo` (`ANC-R30`), que enviesa para cima para dimensionar
 * contrato. Aqui o número aloca energia real: inflar significa mandar para a UC
 * crédito que ela não consome.
 *
 * Meses com consumo zero não entram: UC vazia não descreve o consumo do cliente.
 */
export function mediaObservada(observacoes: ObservacaoMensal[] | null | undefined): MediaObservada {
  const validas = (Array.isArray(observacoes) ? observacoes : []).filter(
    (o) => Number.isFinite(Number(o?.consumoKwh)) && Number(o.consumoKwh) > 0,
  );
  if (validas.length === 0) return { kwh: 0, meses: 0, dispersao: 0 };
  const soma = validas.reduce((acc, o) => acc + Number(o.consumoKwh), 0);
  const media = soma / validas.length;
  // Populacional (÷n), não amostral: a grade É a população dos meses observados,
  // não uma amostra dela.
  const variancia =
    validas.reduce((acc, o) => acc + (Number(o.consumoKwh) - media) ** 2, 0) / validas.length;
  const dispersao = media > 0 ? Math.sqrt(variancia) / media : 0;
  return {
    kwh: Math.round(media),
    meses: validas.length,
    dispersao: Math.round(dispersao * 100) / 100,
  };
}

export interface DecisaoEscrita {
  escrever: boolean;
  motivo: string;
}

/** Variação abaixo disto não justifica reescrever a linha nem gerar auditoria. */
export const TOLERANCIA_ESCRITA_KWH = 1;

/**
 * O job roda todo dia, mas quase todo dia o número é o mesmo — fatura nova só
 * chega uma vez por mês por UC. Escrever mesmo assim geraria `updated_at` diário
 * em toda a carteira e encheria a auditoria de linhas sem informação.
 *
 * A regra crítica é a primeira: leitura vazia NUNCA apaga um valor bom. Uma falha
 * transitória na consulta de faturas zeraria o consumo de UCs saudáveis, e o
 * travamento automático leria esse zero.
 *
 * Vive aqui, e não em `src/lib/`, porque quem mais precisa dela é a rotina diária
 * (Deno, não enxerga `src/`). `consumoOperacional.ts` a reexporta para o front.
 */
export function decidirEscritaDiaria({
  valorPersistido,
  valorCalculado,
  toleranciaKwh = TOLERANCIA_ESCRITA_KWH,
}: {
  valorPersistido: number | null | undefined;
  valorCalculado: number | null | undefined;
  toleranciaKwh?: number;
}): DecisaoEscrita {
  const calculado = Number(valorCalculado);
  const temCalculado = Number.isFinite(calculado) && calculado > 0;

  if (!temCalculado) {
    return { escrever: false, motivo: 'Sem valor calculado nesta rodada — o valor gravado é preservado.' };
  }

  const persistido = Number(valorPersistido);
  if (!Number.isFinite(persistido) || persistido <= 0) {
    return { escrever: true, motivo: 'Primeiro cálculo para esta UC.' };
  }

  const diff = Math.abs(calculado - persistido);
  if (diff < toleranciaKwh) {
    return { escrever: false, motivo: `Variação de ${diff.toFixed(1)} kWh abaixo da tolerância.` };
  }

  return {
    escrever: true,
    motivo: `Consumo mudou de ${Math.round(persistido)} para ${Math.round(calculado)} kWh.`,
  };
}

/** Atalho para a rotina diária: das faturas cruas direto para o número gravado. */
export function consumoObservadoDeFaturas(faturas: FaturaObservada[] | null | undefined): MediaObservada & {
  duplicatasIgnoradas: number;
  mesesNaoReconhecidos: number;
} {
  const { observacoes, duplicatasIgnoradas, mesesNaoReconhecidos } = limparObservacoes(faturas);
  return { ...mediaObservada(observacoes), duplicatasIgnoradas, mesesNaoReconhecidos };
}
